import { supabase } from './supabase';
// import { loadStripe } from '@stripe/stripe-js';
import { /* STRIPE_PUBLISHABLE_KEY, */ getStripePriceId } from './stripe-config';
import { LocalSubscriptionService } from './local-subscription-bypass';

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
  maxChildren?: number;
}

export interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'lifetime';
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
}

interface StripePrice {
  id: string;
  amount: number;
  currency: string;
  interval?: string;
}

interface StripePrices {
  monthly?: StripePrice;
  yearly?: StripePrice;
}

interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  metadata: Record<string, string>;
  prices: StripePrices;
}

// Initialize Stripe (unused but kept for future use)
// let stripePromise: Promise<typeof import('@stripe/stripe-js').Stripe | null> | null = null;
// const getStripe = () => {
//   if (!stripePromise) {
//     stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
//   }
//   return stripePromise;
// };

// Static fallback plans (used when API is unavailable)
// In production, only show parent and family plans
const isProduction = import.meta.env.PROD || window.location.hostname !== 'localhost';
const FALLBACK_PLANS: SubscriptionPlan[] = [
  {
    id: 'parent_plan',
    name: 'Parent Plan',
    price: 5.99,
    interval: 'month',
    maxChildren: 1,
    features: [
      '1 child profile',
      'Access to all activities',
      'Progress tracker',
      'Neuro\'s encouraging feedback',
      'Email support'
    ]
  },
  {
    id: 'family_plan',
    name: 'Family Plan',
    price: 8.99,
    interval: 'month',
    maxChildren: 3,
    features: [
      'Up to 3 children',
      'All Parent Plan features',
      'Printable Packs (Coming Soon)',
      'Advanced progress analytics',
      'Priority support'
    ]
  },
  ...(isProduction ? [] : [{
    id: 'school_starter',
    name: 'School Starter',
    price: 29.00,
    interval: 'month',
    maxChildren: 10,
    features: [
      '10 student seats',
      'SEN dashboard',
      'Reporting tools',
      'Teacher training resources',
      'Monthly or annual billing'
    ]
  }]),
  {
    id: 'lifetime',
    name: 'Lifetime Access',
    price: 0,
    interval: 'month', // Not used for lifetime
    maxChildren: 999, // Unlimited (effectively)
    features: [
      'Unlimited children',
      'All premium features',
      'Lifetime access',
      'Priority support',
      'Early access to new features',
      'Manually granted by admin'
    ]
  }
];

// Dynamic subscription plans loaded from Stripe
export let SUBSCRIPTION_PLANS: SubscriptionPlan[] = [...FALLBACK_PLANS];

export class SubscriptionService {
  static async loadStripeProducts(): Promise<void> {
    try {
      // Try to get session but don't require it for loading products
      const { data: { session } } = await supabase.auth.getSession();
      
      // Call the edge function (products are public, auth optional)
      const headers: Record<string, string> = {};
      if (session) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
      
      const { data, error } = await supabase.functions.invoke('get-stripe-products', {
        headers,
      });

      if (error) {
        console.error('Error loading Stripe products:', error);
        return;
      }

      if (data?.success && data?.products) {
        // Convert Stripe products to our plan format
        const stripePlans: SubscriptionPlan[] = data.products.map((product: StripeProduct) => {
          const monthlyPrice = product.prices?.monthly;
          
          // Use plan_id from metadata if available, otherwise use Stripe product ID
          const planId = product.metadata?.plan_id || product.id;
          
          return {
            id: planId,
            name: product.name,
            price: monthlyPrice ? (monthlyPrice.amount / 100) : 0, // Convert cents to dollars
            interval: 'month' as const,
            maxChildren: this.extractMaxChildrenFromMetadata(product.metadata),
            features: this.extractFeaturesFromMetadata(product.metadata)
          };
        });

        // Sort stripePlans by order metadata before updating global array
        const sortedStripePlans = stripePlans.sort((a, b) => {
          const getOrder = (plan: SubscriptionPlan) => {
            // Try to find order in the original Stripe metadata
            // Since we converted to our plan format, we need to infer order from plan_id
            const orderMap: Record<string, number> = {
              'parent_plan': 1,
              'family_plan': 2, 
              'school_starter': 3
            };
            return orderMap[plan.id] || 999;
          };
          return getOrder(a) - getOrder(b);
        });

        // Update the global plans array (keeping lifetime plan from fallbacks)
        SUBSCRIPTION_PLANS = [...sortedStripePlans, FALLBACK_PLANS.find(p => p.id === 'lifetime')!];
        
        console.log(`✅ Loaded ${stripePlans.length} products from Stripe ${data.cached ? '(cached)' : '(fresh)'}`);
        
        // Log metadata info for debugging
        if (import.meta.env.DEV) {
          console.log('Stripe products metadata:', data.products.map((p: StripeProduct) => ({
            name: p.name,
            metadata: p.metadata
          })));
        }
      }
    } catch (error) {
      console.error('Failed to load Stripe products:', error);
    }
  }

  private static extractMaxChildrenFromMetadata(metadata: Record<string, string>): number {
    if (metadata?.max_children) {
      return parseInt(metadata.max_children);
    }
    // Fallback based on name
    if (metadata?.name?.toLowerCase().includes('parent')) return 1;
    if (metadata?.name?.toLowerCase().includes('family')) return 3;
    if (metadata?.name?.toLowerCase().includes('school')) return 10;
    return 1;
  }

  private static extractFeaturesFromMetadata(metadata: Record<string, string>): string[] {
    if (metadata?.features) {
      try {
        return JSON.parse(metadata.features);
      } catch {
        return metadata.features.split(',').map((f: string) => f.trim());
      }
    }
    // Return basic features as fallback
    return ['Access to all activities', 'Progress tracking', 'Email support'];
  }
  static async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    // Check for local development override first
    if (LocalSubscriptionService.isEnabled()) {
      LocalSubscriptionService.logSubscriptionAction('getUserSubscription', { userId });
      const mockSubscription = LocalSubscriptionService.createMockSubscription(userId);
      if (mockSubscription) {
        return mockSubscription;
      }
    }

    try {      
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        // PGRST116 is "no rows returned" - user has no subscription
        // 42P01 is "relation does not exist" - table doesn't exist yet
        if (error.code === 'PGRST116' || error.code === '42P01') {
          return null;
        }
        console.error('❌ Error fetching user subscription:', error);
        return null;
      }

      // Filter for valid subscription statuses
      if (data && ['active', 'trialing', 'lifetime'].includes(data.status)) {
        return {
          id: data.id,
          userId: data.user_id,
          planId: data.plan_id,
          status: data.status,
          currentPeriodStart: data.current_period_start,
          currentPeriodEnd: data.current_period_end,
          cancelAtPeriodEnd: data.cancel_at_period_end,
          stripeSubscriptionId: data.stripe_subscription_id,
          stripeCustomerId: data.stripe_customer_id
        };
      }

      return null;
    } catch (error) {
      console.error('❌ Error in getUserSubscription:', error);
      return null;
    }
  }

  static async createSubscription(
    userId: string, 
    planId: string, 
    interval: 'monthly' | 'yearly' = 'monthly'
  ): Promise<{ url: string } | null> {
    // Local development bypass
    if (LocalSubscriptionService.isEnabled()) {
      LocalSubscriptionService.logSubscriptionAction('createSubscription', { userId, planId, interval });
      const mockUrl = LocalSubscriptionService.createMockCheckoutUrl(planId, interval);
      return { url: mockUrl };
    }

    try {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Invalid plan ID');
      }

      // Get current user's auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Get Stripe price ID from dynamically loaded plan data
      let priceId: string | null = null;
      
      try {
        const { data } = await supabase.functions.invoke('get-stripe-products', {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        
        if (data?.success && data?.products) {
          const stripeProduct = data.products.find((p: StripeProduct) => 
            p.metadata?.plan_id === planId || p.id === planId
          );
          
          if (stripeProduct?.prices) {
            const targetInterval = interval === 'yearly' ? 'year' : 'month';
            const price = targetInterval === 'year' ? stripeProduct.prices.yearly : stripeProduct.prices.monthly;
            priceId = price?.id;
            console.log(`Found dynamic price ID for ${planId} (${interval}):`, priceId);
          }
        }
      } catch (apiError) {
        console.warn('Failed to get dynamic price, falling back to static config:', apiError);
      }
      
      // Fallback to static configuration if dynamic lookup fails
      if (!priceId) {
        priceId = getStripePriceId(planId, interval);
        console.log(`Using static price ID for ${planId} (${interval}):`, priceId);
      }
      
      if (!priceId) {
        throw new Error(`Price ID not found for plan ${planId} and interval ${interval}`);
      }

      // Call Supabase Edge Function to create Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          interval,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/subscription/cancel`,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error creating checkout session:', error);
        return null;
      }

      return { url: data.url };
    } catch (error) {
      console.error('Error in createSubscription:', error);
      return null;
    }
  }

  static async cancelSubscription(
    userId: string, 
    cancelImmediately: boolean = false
  ): Promise<{ success: boolean; canceledImmediately?: boolean; currentPeriodEnd?: string }> {
    // Local development bypass
    if (LocalSubscriptionService.isEnabled()) {
      LocalSubscriptionService.logSubscriptionAction('cancelSubscription', { userId, cancelImmediately });
      return { 
        success: true, 
        canceledImmediately: cancelImmediately,
        currentPeriodEnd: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // Tomorrow
      };
    }

    try {
      // Get current user's auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Call Supabase Edge Function to cancel Stripe subscription
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: {
          cancelImmediately,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error canceling subscription:', error);
        return { success: false };
      }

      return {
        success: true,
        canceledImmediately: data.canceledImmediately,
        currentPeriodEnd: data.currentPeriodEnd,
      };
    } catch (error) {
      console.error('Error in cancelSubscription:', error);
      return { success: false };
    }
  }

  static async createPortalSession(): Promise<{ url: string } | null> {
    // Local development bypass
    if (LocalSubscriptionService.isEnabled()) {
      LocalSubscriptionService.logSubscriptionAction('createPortalSession');
      const mockUrl = LocalSubscriptionService.createMockPortalUrl();
      return { url: mockUrl };
    }

    try {
      // Get current user's auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('User not authenticated');
      }

      // Call Supabase Edge Function to create Stripe portal session
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: {
          returnUrl: `${window.location.origin}/subscription/manage`,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error creating portal session:', error);
        return null;
      }

      return { url: data.url };
    } catch (error) {
      console.error('Error in createPortalSession:', error);
      return null;
    }
  }

  static getPlanById(planId: string): SubscriptionPlan | undefined {
    return SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
  }

  static canAccessFeature(subscription: UserSubscription | null, feature: string): boolean {
    if (!subscription || (subscription.status !== 'active' && subscription.status !== 'trialing' && subscription.status !== 'lifetime')) {
      return false;
    }

    const plan = this.getPlanById(subscription.planId);
    return plan?.features.includes(feature) || false;
  }

  static getMaxChildren(subscription: UserSubscription | null): number {
    // Local development override
    const localOverride = LocalSubscriptionService.getMaxChildrenOverride();
    if (localOverride !== null) {
      LocalSubscriptionService.logSubscriptionAction('getMaxChildren', { localOverride });
      return localOverride;
    }

    if (!subscription || (subscription.status !== 'active' && subscription.status !== 'trialing' && subscription.status !== 'lifetime')) {
      return 0;
    }

    const plan = this.getPlanById(subscription.planId);
    return plan?.maxChildren || 0;
  }
}

export default SubscriptionService;